import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { CONTACT, AREAS_SERVED } from "../constants/data";

const QUICK_LINKS = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Services & Rates", to: "/services-and-rates" },
  { label: "Care Process", to: "/care-process" },
  { label: "Why Choose Us", to: "/why-choose-us" },
  { label: "Patient Referral", to: "/referral" },
  { label: "Contact Us", to: "/contact" },
];

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-brand-navy text-white">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-10">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div>
          <div className="inline-block bg-white rounded-2xl p-3 shadow-[0_8px_30px_rgb(0,0,0,0.25)]">
            <img src="/assets/bms-logo.png" alt="BMS Health Care Services logo" className="h-20 w-auto" />
          </div>
          <p className="mt-6 text-[15px] leading-relaxed text-slate-300">
            Helping individuals live safely, comfortably, and independently at home through compassionate, personalized care.
          </p>
          <p className="mt-5 font-display italic text-brand-cyan tracking-wide text-sm">
            Compassion · Dignity · Respect · Excellence
          </p>
        </div>

        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-brand-cyan">Quick Links</h3>
          <ul className="mt-5 space-y-3">
            {QUICK_LINKS.map((l) => (
              <li key={l.label}>
                <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-[15px] text-slate-300 hover:text-white transition-colors duration-300">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-brand-cyan">Contact</h3>
          <ul className="mt-5 space-y-4 text-[15px] text-slate-300">
            <li className="flex gap-3">
              <Phone className="h-4 w-4 mt-0.5 text-brand-cyan shrink-0" strokeWidth={1.8} />
              <a href={CONTACT.phoneHref} data-testid="footer-phone" className="hover:text-white transition-colors duration-300">{CONTACT.phone}</a>
            </li>
            <li className="flex gap-3">
              <Mail className="h-4 w-4 mt-0.5 text-brand-cyan shrink-0" strokeWidth={1.8} />
              <a href={CONTACT.emailHref} data-testid="footer-email" className="hover:text-white transition-colors duration-300 break-all">{CONTACT.email}</a>
            </li>
            <li className="flex gap-3">
              <MapPin className="h-4 w-4 mt-0.5 text-brand-cyan shrink-0" strokeWidth={1.8} />
              <span>204/8 – 13049 76 Avenue<br />Surrey, BC V3W 2V7</span>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-brand-cyan">Areas Served</h3>
          <p className="mt-5 text-sm leading-loose text-slate-300">{AREAS_SERVED.join(" · ")}</p>
          <Link
            to="/referral"
            data-testid="footer-referral-cta"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-brand-cyan/60 px-5 py-2.5 text-sm font-bold text-brand-cyan hover:bg-brand-cyan hover:text-brand-navy transition-colors duration-300 active:scale-95"
          >
            Refer a Patient
          </Link>
        </div>
      </div>

      <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-slate-400">© {new Date().getFullYear()} BMS Health Care Services Ltd. All rights reserved.</p>
        <div className="flex flex-wrap items-center justify-center sm:justify-end gap-x-6 gap-y-2">
          <Link to="/privacy" data-testid="footer-privacy-link" className="text-xs text-slate-400 hover:text-white transition-colors duration-300">Privacy Policy</Link>
          <Link to="/referral" data-testid="footer-referral-link" className="text-xs text-slate-400 hover:text-white transition-colors duration-300">Patient Referral</Link>
          <Link to="/portal/login" data-testid="footer-admin-login-link" className="text-xs text-slate-400 hover:text-white transition-colors duration-300">Office Admin Login</Link>
          <Link to="/caregiver/login" data-testid="footer-caregiver-login-link" className="text-xs text-slate-400 hover:text-white transition-colors duration-300">Caregiver Login</Link>
          <Link to="/patient/login" data-testid="footer-patient-login-link" className="text-xs text-slate-400 hover:text-white transition-colors duration-300">Patient Login</Link>
        </div>
      </div>
    </div>
  </footer>
);
